import {
    confirmSignUp as amplifyConfirmSignUp,
    resetPassword as amplifyResetPassword,
    signIn as amplifySignIn,
    signOut as amplifySignOut,
    signUp as amplifySignUp,
    fetchAuthSession,
    resendSignUpCode,
    signInWithRedirect,
} from 'aws-amplify/auth';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  company?: string;
  address?: string;
  createdAt?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithPhone: (phoneNumber: string) => Promise<void>;
  confirmPhoneSignIn: (phoneNumber: string, code: string) => Promise<void>;
  updateProfile: (profileData: Partial<User>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const id = tokens?.idToken?.payload?.sub as string | undefined;
      if (id) {
        const payload = tokens!.idToken!.payload as any;
        setUser({
          id,
          email: (payload.email as string) || '',
          firstName: (payload.name as string) || undefined,
        });
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Error checking user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (email: string, password: string) => {
    try {
      const { isSignedIn } = await amplifySignIn({ username: email, password });
      if (isSignedIn) {
        await checkUser();
      }
    } catch (error) {
      throw new Error('Échec de la connexion');
    }
  };

  const handleSignUp = async (email: string, password: string, name: string) => {
    try {
      await amplifySignUp({
        username: email,
        password,
        options: { userAttributes: { email, name } },
      });
    } catch (error) {
      throw new Error("Échec de l'inscription");
    }
  };

  const handleConfirmSignUp = async (email: string, code: string) => {
    try {
      await amplifyConfirmSignUp({ username: email, confirmationCode: code });
    } catch (error) {
      throw new Error('Échec de la confirmation');
    }
  };

  const handleResendConfirmationCode = async (email: string) => {
    try {
      await resendSignUpCode({ username: email });
    } catch (error) {
      throw new Error("Échec de l'envoi du code de confirmation");
    }
  };

  const handleSignOut = async () => {
    try {
      await amplifySignOut();
      setUser(null);
    } catch (error) {
      throw new Error('Échec de la déconnexion');
    }
  };

  const handleResetPassword = async (email: string) => {
    try {
      await amplifyResetPassword({ username: email });
    } catch (error) {
      throw new Error('Échec de la réinitialisation du mot de passe');
    }
  };

  const handleSignInWithGoogle = async () => {
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      throw new Error('Échec de la connexion avec Google');
    }
  };

  const handleSignInWithPhone = async (phoneNumber: string) => {
    // Optional: keep local phone flow as-is for now
    try {
      localStorage.setItem('pending-phone', phoneNumber);
    } catch (error) {
      throw new Error("Échec de l'envoi du code de vérification");
    }
  };

  const handleConfirmPhoneSignIn = async (phoneNumber: string, code: string) => {
    // Keep demo behavior for phone for now
    try {
      const mockUser: User = {
        id: '1',
        email: `${phoneNumber}@phone.com`,
        firstName: 'Utilisateur',
        lastName: 'Téléphone',
        phone: phoneNumber,
        createdAt: new Date().toISOString(),
      };
      localStorage.setItem('totalbat-user', JSON.stringify(mockUser));
      localStorage.removeItem('pending-phone');
      setUser(mockUser);
    } catch (error) {
      throw new Error('Code de vérification invalide');
    }
  };

  const handleUpdateProfile = async (profileData: Partial<User>) => {
    try {
      if (!user) {
        throw new Error('Utilisateur non connecté');
      }
      
      // Update user data
      const updatedUser = { ...user, ...profileData };
      
      // Store updated user in localStorage
      localStorage.setItem('totalbat-user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      throw new Error('Échec de la mise à jour du profil');
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signIn: handleSignIn,
    signUp: handleSignUp,
    signOut: handleSignOut,
    resetPassword: handleResetPassword,
    confirmSignUp: handleConfirmSignUp,
    resendConfirmationCode: handleResendConfirmationCode,
    signInWithGoogle: handleSignInWithGoogle,
    signInWithPhone: handleSignInWithPhone,
    confirmPhoneSignIn: handleConfirmPhoneSignIn,
    updateProfile: handleUpdateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 